import React, { useState } from "react";
import { Link, useLoaderData, useNavigate, useParams } from 'react-router-dom';
import {
  NJButton,
  NJFormItem,
  NJSidebarDeprecated as NJSidebar,
  NJSidebarBrandDeprecated as NJSidebarBrand,
  NJSidebarDividerDeprecated as NJSidebarDivider,
  NJSidebarMenuDeprecated as NJSidebarMenu,
  NJSidebarContentDeprecated as NJSidebarContent,
  NJInlineMessage,
  NJSpinner,
  NJBadge,
  NJToastService,
} from "@engie-group/fluid-design-system-react";
import { useMutation } from "@tanstack/react-query";

import { useForm, formState } from "@genai/form";
import { SidebarLink, SidebarFooter } from "@genai/sidebar";

import styles from "./page.module.css";
import { Organization, OrganizationId, CreateOrganizationForm } from "../model";
import { LoaderData } from "../loaders";
import { createOrganization } from "./createOrganization";
import { editOrganization } from "./editOrganization";
import { deleteOrganization } from "./deleteOrganization";
import { ConfirmationModal } from "@genai/confirmation";
import { joinOrganization } from './joinOrganization.ts';

export const OrganizationPage = (): React.ReactElement => {
  const { organizationId } = useParams() as { organizationId: OrganizationId };
  const navigate = useNavigate();
  const loaderData = useLoaderData() as LoaderData;
  const [organizations, setOrganizations] = useState<Organization[]>(
    loaderData.organizations || [],
  );

  const currentOrganization = organizations.filter(
    (organization) => organization.uuid === organizationId,
  )[0];

  const [currentOrganizationUser, setCurrentOrganizationUser] = useState<string>(
    loaderData.user.organization || "",
  );

  const [name, setName] = useState<string>(currentOrganization?.name || "");
  const [description, setDescription] = useState<string>(
    currentOrganization?.description || "",
  );

  const organizationToForm = (organization: Organization | null) => {
    setName(organization?.name || "");
    setDescription(organization?.description || "");
  };

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);

  const [isConfirmationJoinModalOpen, setIsConfirmationJoinModalOpen] =
    useState<boolean>(false);

  const { isPending: isEditPending, mutate: editOrganizationMutate } =
    useMutation({
      mutationFn: editOrganization.bind(this, organizationId),
      onSuccess: () => {
        NJToastService("Organisation modifiée avec succès !");
      },
      onError: (error) => {
        NJToastService(`Une erreur est survenue : ${error.message}`);
      },
    });

  const { isPending: isCreatePending, mutate: createOrganizationMutate } =
    useMutation({
      mutationFn: createOrganization,
      onSuccess: (newOrganization) => {
        setOrganizations([...organizations, newOrganization as Organization]);
        NJToastService("Organisation créée avec succès !");
      },
      onError: (error) => {
        NJToastService(`Une erreur est survenue : ${error.message}`);
      },
    });

  const { isPending: isDeletePending, mutate: deleteOrganizationMutate } =
    useMutation({
      mutationFn: deleteOrganization.bind(null, organizationId),
      onSuccess: () => {
        setOrganizations([
          ...organizations.filter(
            (organization) => organization.uuid !== organizationId,
          ),
        ]);
        organizationToForm(null);
        navigate("/admin/organization");
        NJToastService("Organisation supprimée avec succès !");
      },
      onError: (error) => {
        NJToastService(`Une erreur est survenue : ${error.message}`);
      },
    });

  const { isPending: isJoinPending, mutate: joinOrganizationMutate } =
    useMutation({
      mutationFn: joinOrganization.bind(null, organizationId, loaderData.user.uuid, loaderData.user.role , loaderData.user.is_active),
      onSuccess: () => {
        setCurrentOrganizationUser(organizationId);
        NJToastService("Vous avez bien rejoins l'organisation !");
      },
      onError: (error) => {
        NJToastService(`Une erreur est survenue : ${error.message}`);
      },
    });

  const isPending = isEditPending || isCreatePending || isDeletePending || isJoinPending;

  const { formStatus, errors, onSubmit } = useForm<CreateOrganizationForm>({
    handleSubmit: organizationId
      ? editOrganizationMutate
      : createOrganizationMutate,
    requiredFields: {
      name: "Nom requis",
      description: "Description requise",
    },
  });

  return (
    <>
      <NJSidebar isFolded={false}>
        <NJSidebarBrand imageSrc="/techhelp-logo.png" />
        <NJSidebarMenu>
          <div>
            <div className="genai-block_btn_new" >
              <Link
                className="genai-sidebar-new"
                to={`/admin/organization`}
              >
                <NJButton
                  className="genai-sidebar-btn-new"
                  icon="add_circle_outline"
                  label="Nouvelle Organisation"
                  variant="secondary"
                />
              </Link>
            </div>
          </div>
          <NJSidebarDivider className="genai-sidebar-divider" />
          <div className="genai-sidebar-menu-content">
            <h4 className={styles["sidebar-content-title"]}>
              Liste des organisations
            </h4>
            {organizations.map((organization) => (
              <SidebarLink
                className={styles["sidebar_link"]}
                key={organization.uuid}
                active={organizationId === organization.uuid}
                onClick={() => organizationToForm(organization)}
                to={`/admin/organization/${organization.uuid}`}
              >
                {organization.name}

                {currentOrganizationUser === organization.uuid && (
                  <div className={styles["current_organisation_label"]}>
                    <NJBadge scale="sm" iconName="done" variant="information">
                    </NJBadge>
                  </div>
                )}
              </SidebarLink>
            ))}
          </div>
        </NJSidebarMenu>
        <SidebarFooter onNavigate={() => organizationToForm(null)} />
      </NJSidebar>
      <NJSidebarContent className={styles["sidebar-content"]}>
        <div className={styles["sidebar-content-wrapper"]}>
          {!organizationId ? (
            <h1>Créer une nouvelle organisation</h1>
          ) : (
            <h1>Modifier l'organisation</h1>
          )}

          {organizationId === currentOrganizationUser && (
            <NJInlineMessage variant="information" className={styles["message_info"]}>
            Vous êtes actuellement dans cette organisation.
            </NJInlineMessage>
          )}

          <form
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit({
                name,
                description: description,
              });
            }}
          >
            <NJFormItem
              id="name"
              label="Nom de l'organisation"
              value={name}
              isRequired
              disabled={!!organizationId}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const target = event.target as HTMLInputElement;
                setName(target.value || "");
              }}
            />
            <NJFormItem
              id="description"
              label="Description"
              isRequired
              isMultiline={true}
              value={description}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const target = event.target as HTMLInputElement;
                setDescription(target.value || "");
              }}
            />
            <div className={styles["validation-container"]}>
              <p>
                {formStatus === formState.error
                  ? errors.map((error) => (
                      <span key={crypto.randomUUID()}>
                        - {error} <br />
                      </span>
                    ))
                  : null}
              </p>
              <div
                style={{ display: "flex", alignItems: "center", gap: "16px" }}
              >
                {isPending && <NJSpinner scale="xs"  aria-label="" /> }
                {organizationId && (
                  <>
                    <ConfirmationModal
                      content="Êtes-vous sûr de vouloir supprimer l'organisation ?"
                      isOpen={isConfirmationModalOpen}
                      onClose={(confirm) => {
                        if (confirm) {
                          deleteOrganizationMutate();
                        }
                        setIsConfirmationModalOpen(false);
                      }}
                    ></ConfirmationModal>
                    <ConfirmationModal
                      content="Êtes-vous sûr de vouloir rejoindre l'organisation ?"
                      isOpen={isConfirmationJoinModalOpen}
                      onClose={(confirm) => {
                        if (confirm) {
                          joinOrganizationMutate()
                        }
                        setIsConfirmationJoinModalOpen(false);
                      }}
                    ></ConfirmationModal>
                    <NJButton
                      variant="destructive"
                      label="Supprimer"
                      onClick={setIsConfirmationModalOpen.bind(null, true)}
                    ></NJButton>
                    <NJButton
                      disabled={organizationId === currentOrganizationUser}
                      onClick={setIsConfirmationJoinModalOpen.bind(null, true)}
                      label="Rejoindre l'organisation"
                    />
                  </>
                )}
                <NJButton
                  disabled={isPending}
                  type="submit"
                  label="Enregistrer l'organisation"
                />
              </div>
            </div>
          </form>
        </div>
      </NJSidebarContent>
    </>
  );
};
